import { useEffect, useState } from "react";
import { Star, ShieldCheck, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

export function Stars({ value = 0, size = "h-4 w-4", onRate }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type={onRate ? "button" : undefined}
          disabled={!onRate}
          onClick={onRate ? () => onRate(n) : undefined}
          data-testid={onRate ? `rate-star-${n}` : undefined}
          className={onRate ? "cursor-pointer" : "cursor-default"}
          aria-label={`${n} star`}
        >
          <Star className={`${size} ${n <= Math.round(value) ? "fill-ink text-ink" : "fill-transparent text-ink/30"}`} />
        </button>
      ))}
    </div>
  );
}

export function ProductReviews({ productId }) {
  const [data, setData] = useState({ reviews: [], summary: { count: 0, avg: 0, distribution: {} } });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ rating: 0, name: "", order_id: "", email: "", title: "", body: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get(`/products/${productId}/reviews`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [productId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.rating) { toast.error("Please pick a star rating"); return; }
    if (!form.order_id.trim()) { toast.error("Enter your order number"); return; }
    setSubmitting(true);
    try {
      await api.post("/reviews", { product_id: productId, ...form });
      toast.success("Thanks! Your verified review is published.");
      setForm({ rating: 0, name: "", order_id: "", email: "", title: "", body: "" });
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit review");
    } finally { setSubmitting(false); }
  };

  const { count, avg, distribution = {} } = data.summary;

  return (
    <div className="mt-10 border-t-2 border-ink pt-8" data-testid="product-reviews">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-heading font-black text-2xl tracking-tight">Verified Buyer Reviews</h2>
          {count > 0 ? (
            <div className="flex items-center gap-3 mt-2">
              <span className="font-heading font-black text-3xl">{avg.toFixed(1)}</span>
              <div>
                <Stars value={avg} />
                <div className="font-mono text-[11px] text-subtle mt-0.5">{count} verified review{count === 1 ? "" : "s"}</div>
              </div>
            </div>
          ) : (
            <p className="font-mono text-sm text-subtle mt-2 max-w-md">No reviews yet — only shoppers who actually bought this item can review it. Be the first verified buyer.</p>
          )}
        </div>
        <button data-testid="write-review-toggle" onClick={() => setOpen((o) => !o)} className="inline-flex items-center gap-2 border-2 border-ink px-4 h-10 font-mono text-[11px] uppercase tracking-wider hover:bg-ink hover:text-paper transition-colors">
          Write a Review <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      </div>

      {count > 0 && (
        <div className="mt-4 max-w-xs space-y-1">
          {[5, 4, 3, 2, 1].map((r) => {
            const c = distribution[r] || 0;
            const pct = count ? (c / count) * 100 : 0;
            return (
              <div key={r} className="flex items-center gap-2 font-mono text-[11px]">
                <span className="w-3 text-subtle">{r}</span>
                <Star className="h-3 w-3 fill-ink text-ink" />
                <div className="flex-1 h-2 bg-surface2 border border-ink/20"><div className="h-full bg-ink" style={{ width: `${pct}%` }} /></div>
                <span className="w-6 text-right text-subtle">{c}</span>
              </div>
            );
          })}
        </div>
      )}

      {open && (
        <form onSubmit={submit} data-testid="review-form" className="mt-6 border-2 border-ink p-5 space-y-4 bg-surface/40">
          <p className="font-mono text-[11px] text-subtle">Only verified purchasers can review. Enter the order number from your confirmation email (and the email you used at checkout).</p>
          <div className="flex items-center gap-3">
            <span className="font-mono text-[11px] uppercase tracking-wider text-subtle">Your rating</span>
            <Stars value={form.rating} size="h-6 w-6" onRate={(n) => set("rating", n)} />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <input data-testid="review-order" value={form.order_id} onChange={(e) => set("order_id", e.target.value)} placeholder="Order number *" className="border border-ink h-10 px-3 bg-paper text-sm font-mono" />
            <input data-testid="review-email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="Email used at checkout" className="border border-ink h-10 px-3 bg-paper text-sm font-mono" />
            <input data-testid="review-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Display name (optional)" className="border border-ink h-10 px-3 bg-paper text-sm font-mono" />
            <input data-testid="review-title" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Headline (optional)" className="border border-ink h-10 px-3 bg-paper text-sm font-mono" />
          </div>
          <textarea data-testid="review-body" value={form.body} onChange={(e) => set("body", e.target.value)} rows={3} placeholder="Tell other STR operators how it worked out…" className="w-full border border-ink p-3 bg-paper text-sm font-mono" />
          <button data-testid="review-submit" disabled={submitting} className="bg-ink text-paper h-10 px-6 font-mono text-[11px] uppercase tracking-wider hover:opacity-90 disabled:opacity-40">{submitting ? "Submitting…" : "Submit Review"}</button>
        </form>
      )}

      <div className="mt-8 space-y-6" data-testid="review-list">
        {data.reviews.map((r) => (
          <div key={r.id} className="border-b border-ink/15 pb-6" data-testid={`review-${r.id}`}>
            <div className="flex items-center gap-3">
              <Stars value={r.rating} />
              {r.verified && <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-success"><ShieldCheck className="h-3.5 w-3.5" /> Verified buyer</span>}
            </div>
            {r.title && <div className="font-heading font-black text-base mt-2">{r.title}</div>}
            {r.body && <p className="font-mono text-sm text-subtle leading-relaxed mt-1">{r.body}</p>}
            <div className="font-mono text-[11px] text-subtle/70 mt-2">{r.name} · {new Date(r.created_at).toLocaleDateString()}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
